import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

type Storage = 'session' | 'local';

type UsePersistedStateOptions<T> = {
  storage?: Storage;
  /** Convert the stored string back to `T`. Defaults to `JSON.parse`. */
  deserialize?: (raw: string) => T;
  /** Convert `T` to a storable string. Defaults to `JSON.stringify`. */
  serialize?: (value: T) => string;
};

function getStorage(kind: Storage): globalThis.Storage | null {
  try {
    return kind === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    // Private browsing and hardened settings can make the accessor itself throw.
    return null;
  }
}

/**
 * `useState` that persists to session or local storage.
 *
 * Replaces five hand-rolled read-on-init / write-on-change pairs. Each of those
 * read storage inline in a `useState` initializer with no guard, so corrupt JSON
 * or a blocked storage accessor would throw during render.
 *
 * Re-reads when `key` changes, which matters for the per-trip keys
 * (`itinerary-sidebar:{tripId}`): the previous implementations kept the old
 * trip's value until remount.
 *
 * Pass `key: null` to skip persistence entirely while an id is still loading.
 */
export function usePersistedState<T>(
  key: string | null,
  initial: T | (() => T),
  { storage = 'session', deserialize, serialize }: UsePersistedStateOptions<T> = {}
): [T, Dispatch<SetStateAction<T>>] {
  const resolveInitial = useCallback(
    () => (typeof initial === 'function' ? (initial as () => T)() : initial),
    [initial]
  );

  const read = useCallback(
    (storageKey: string | null): T => {
      if (!storageKey) {
        return resolveInitial();
      }
      const store = getStorage(storage);
      if (!store) {
        return resolveInitial();
      }
      try {
        const raw = store.getItem(storageKey);
        if (raw === null) {
          return resolveInitial();
        }
        return deserialize ? deserialize(raw) : (JSON.parse(raw) as T);
      } catch {
        // Corrupt or unparseable — fall back rather than throwing mid-render.
        return resolveInitial();
      }
    },
    [resolveInitial, storage, deserialize]
  );

  const [value, setValue] = useState<T>(() => read(key));

  // Re-sync when the key changes (e.g. navigating between trips).
  const previousKey = useRef(key);
  useEffect(() => {
    if (previousKey.current !== key) {
      previousKey.current = key;
      setValue(read(key));
    }
  }, [key, read]);

  useEffect(() => {
    if (!key) {
      return;
    }
    const store = getStorage(storage);
    if (!store) {
      return;
    }
    try {
      store.setItem(key, serialize ? serialize(value) : JSON.stringify(value));
    } catch {
      // Quota exceeded or storage blocked; the in-memory value still works.
    }
  }, [key, value, storage, serialize]);

  return [value, setValue];
}

export default usePersistedState;
